import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CareerFocusWithMetrics } from '../../hooks/useCareerFocus';
import { useAllActiveFocuses } from '../../hooks/useAllActiveFocuses';
import { useDefaultFocus } from '../../hooks/useDefaultFocus';

interface FocusModeSliderProps {
  isActive?: boolean;
  activeFocus?: CareerFocusWithMetrics | null;
  onToggle?: () => void;
  className?: string;
}

export const FocusModeSlider: React.FC<FocusModeSliderProps> = ({
  isActive: isActiveProp,
  activeFocus,
  onToggle: onToggleProp,
  className = ''
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeFocuses } = useAllActiveFocuses();
  const { defaultFocusId } = useDefaultFocus();
  const [isActive, setIsActive] = useState(false);

  // Determine current mode from URL - now includes /focus (hub)
  useEffect(() => {
    if (location.pathname === '/focus' || location.pathname.startsWith('/focus/')) {
      setIsActive(true);
    } else {
      setIsActive(false);
    }
  }, [location.pathname]);

  const handleToggle = () => {
    if (onToggleProp) {
      onToggleProp();
    } else {
      // New Focus Hub navigation logic
      if (isActive) {
        // Turn off - go to home
        navigate('/home');
      } else {
        // Turn on - set flag to indicate this came from toggle
        sessionStorage.setItem('focus-mode-toggle', 'true');
        
        // Go to default focus or hub
        if (defaultFocusId && activeFocuses && activeFocuses.length > 0) {
          const defaultFocus = activeFocuses.find(f => f.id === defaultFocusId);
          if (defaultFocus) {
            navigate(`/focus/${defaultFocus.focus_type}/${defaultFocus.id}`);
            return;
          }
        }
        // No default or default not found - go to hub
        navigate('/focus');
      }
    }
  };
  return (
    <div className={`focus-mode-slider flex flex-col items-center gap-0.5 ${className}`}>
      {/* FOCUS text */}
      <span 
        className="text-[11px] font-light tracking-[0.2em] uppercase select-none cursor-pointer"
        style={{ 
          color: isActive ? '#3B82F6' : '#9CA3AF',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          letterSpacing: '0.2em'
        }}
        onClick={handleToggle}
      >
        Focus
      </span>
      
      {/* Slider */}
      <button
        onClick={handleToggle}
        className="relative w-10 h-5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        style={{
          backgroundColor: isActive ? '#3B82F6' : '#E5E7EB'
        }}
        aria-label={isActive ? 'Disable focus mode' : 'Enable focus mode'}
        aria-checked={isActive}
        role="switch"
      >
        <div
          className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-300"
          style={{
            transform: isActive ? 'translateX(1.25rem)' : 'translateX(0.125rem)'
          }}
        />
      </button>
      
      {/* Status text - ON/OFF */}
      <span 
        className={`text-[10px] mt-0.5 font-medium ${
          isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'
        }`}
        style={{ letterSpacing: '0.1em' }}
      >
        {isActive ? 'ON' : 'OFF'}
      </span>
    </div>
  );
};